<template>
  <div role="form" class="profile-info" >
    <div class="profile-info__row">
      <div class="profile-info__form-group">
        <label>{{ getText('name') }}*</label>
        <b-input
          v-model="profile.name"
          maxlength="80"
          v-bind="nameValidationContext.input"
          @blur.native="nameValidationContext.inputEvent.blurNative"
        />
        <s-validation-tooltip
          :validationContext="nameValidationContext"/>
      </div>

      <div class="profile-info__form-group">
        <label>
          {{ getText('origin') }}*
          <i
            aria-hidden="true"
            v-b-tooltip.hover.right="getText('origin-profile-tooltip') + '.'"
            class="fas fa-info-circle profile-info__origin-profile-icon"
          />
        </label>
        <s-select
          v-model="profile.parentProfileId"
          text-field="name"
          value-field="id"
          :disabled="disableOriginProfile"
          :options="profiles"
          :state="$bsStateValidator($v.profile.parentProfileId.$error)"
          @blur="$v.profile.parentProfileId.$touch()"
        />
      </div>
    </div>

    <div class="profile-info__form-group">
      <label>{{ getText('description') }}</label>
      <b-input
        v-model="profile.description"
        maxlength="200"
      />
    </div>

    <div class="profile-info__form-group">
      <label>{{ getText('managers') }}</label>
      <i
        aria-hidden="true"
        v-b-tooltip.hover.right="getText('managers-profile-tooltip') + '.'"
        class="fas fa-info-circle profile-info__origin-profile-icon"
      />
      <s-multiselect
        v-model="profile.administrators"
        :is-tag-with-no-remove="isTagWithNoRemove"
        :proxy-binds="{
          options: administrators,
          class: { multiselect_invalid: $v.profile.administrators.$error },
        }"
        :proxy-events="{
          remove: onRemoveTag,
          close: () => $v.profile.administrators.$touch(),
        }"
      />
    </div>

    <div class="profile-info__form-group">
      <label>{{ getText('users.title') }}</label>
      <s-table
        class="profile-info__table"
        :fields="userTableFields"
        :items="profile.users"
      >
        <template #cell(status)="data">
          <status-column :item="data.item" />
        </template>
      </s-table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required, minLength } from 'vuelidate/lib/validators';
import { createValidationContext } from '@/services/validationHelper';
import ProfileEditService from '@/domain/services/generalPanel/adminPanel/profile/profileEditService';
import comparator from '@/services/comparationHelpers';
import StatusColumn from '../StatusColumn';

export function uniqueNameValidator() {
  return !this.uniqueNameValidationLoading && this.uniqueName;
}

export default {
  components: {
    StatusColumn,
  },
  props: {
    profile: Object,
    valid: Boolean,
    originParentProfile: {
      type: Object,
      required: false,
    },
  },
  data: () => ({
    profiles: [],
    administrators: [],
    uniqueName: true,
    uniqueNameAlreadyVerified: true,
    uniqueNameValidationLoading: false,
  }),
  validations: {
    profile: {
      name: {
        required,
        minLength: minLength(2),
        mustBeUnique: uniqueNameValidator,
      },
      parentProfileId: { required },
      administrators: {
        required,
        minLength: minLength(1),
      },
    },
  },
  methods: {
    onRemoveTag(tag) {
      if (comparator.compareIds(tag.id, this.loggedUserAsAdmin.id)) {
        this.$pushAlert({ code: 'MA028' });
      }
    },
    isSameUser(tag = {}) {
      this.uniqueUsernameAlreadyVerified = false;
      const tagId = (tag.option || {}).id || tag.id;
      return comparator.compareIds(tagId, this.loggedUserAsAdmin.id);
    },
    isTagWithNoRemove(tag) {
      return this.isSameUser(tag) && !this.isEdit;
    },
    getText(key) {
      return this.$t(`profile-manager.editing.info.${key}`);
    },
    async checkNameAvailable() {
      this.uniqueName = true;
      this.uniqueNameValidationLoading = true;
      const { name, id: profileId } = this.profile;
      const response = await ProfileEditService
        .verifyIfProfileNameIsAvailable(name, profileId);
      this.uniqueNameAlreadyVerified = true;
      this.uniqueNameValidationLoading = false;

      if (!response.available) {
        this.uniqueName = false;
      }
    },
  },
  computed: {
    ...mapGetters(['user']),
    isEdit() {
      return (this.$route.params || {}).profileId;
    },
    loggedUserAsAdmin() {
      return {
        id: this.user.id.toUpperCase(),
        name: this.user.username,
      };
    },
    isValid() {
      return !this.$v.$invalid;
    },
    userTableFields() {
      const prefix = 'profile-manager.editing.info.users';

      return [
        {
          key: 'name',
          label: this.$t(`${prefix}.name`),
        },
        {
          key: 'username',
          label: this.$t(`${prefix}.username`),
        },
        {
          key: 'status',
          label: this.$t(`${prefix}.status`),
          class: 'text-center',
        },
      ];
    },
    nameValidationTooltip() {
      const validationModel = this.$v.profile.name;
      if (!validationModel.required || !validationModel.minLength) {
        return this.$t('messages.MG001.message', { 0: 2, 1: 80 });
      }
      if (!this.uniqueName) {
        return this.$t('messages.MG004.message');
      }
      return false;
    },
    disableOriginProfile() {
      return Boolean(this.profile.id)
        || this.profile.companies.filter(company => company.selected).length > 0;
    },
    disableNextButton() {
      return this.$v.$invalid
        || !this.uniqueNameAlreadyVerified
        || !this.uniqueNameValidationLoading;
    },
    canForward() {
      return !this.disableNextButton;
    },
    ...createValidationContext({
      name: {
        validationModel: vm => vm.$v.profile.name,
        validationMessage: vm => vm.nameValidationTooltip,
        blurNativeCallback: vm => vm.checkNameAvailable(),
      },
    }),
  },
  created() {
    ProfileEditService.getProfiles()
      .then((response) => {
        ProfileEditService.tryIncludeParent(this.originParentProfile, response);
        this.profiles = [{ id: null, name: this.$t('general.select') }].concat(response);
      });
    ProfileEditService.getEligibleAdministrators()
      .then((response) => {
        this.administrators = response;
      })
      .finally(() => {
        if (!this.profile.id && !this.profile.administrators.length) {
          this.administrators.push(this.loggedUserAsAdmin);
          this.profile.administrators.push(this.loggedUserAsAdmin);
        }

        if (this.isEdit) {
          this.profile.parentProfileId = this.profile.originParentProfile.id;
        }
      });
  },
  watch: {
    isValid(value) {
      this.$emit('update:valid', value);
    },
    originParentProfile() {
      ProfileEditService.tryIncludeParent(this.originParentProfile, this.profiles);
    },
    canForward(newValue) {
      this.$emit('can-forward-changed', newValue);
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  ::v-deep
    .multiselect__no-remove-tag
      font-size: 1rem
      padding-top: 0
      padding-bottom: 0

  .profile-info
    &__row
      display: flex
      flex-direction: column
    &__form-group
      flex: 1
      margin: 12px
    &__origin-profile-icon
      color: var(--color-info)
    &__table ::v-deep
      display: flex
      table
        display: flex
        flex-direction: column
      tbody
        display: block
        max-height: 190px
        overflow: auto
        tr
          word-wrap: break-word
      tbody, thead
        tr
          display: table
          width: 100%
          table-layout: fixed
      .empty-list
        padding: 2rem 0 !important
    ::v-deep
      .multiselect__tags-wrap
        display: inline-flex
        flex-wrap: wrap
      .multiselect__tag
        height: 24px
    +media-min-sm()
      &__row
        flex-direction: row
    +media-min-md()
    +media-min-lg()
</style>
